import React, { useState } from 'react';
import { Alert, Card, Form } from 'react-bootstrap';
import Select from 'react-select';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { ResponseError } from '../../../common/pokCore/autogenerated/pokApiClient';
import { Waiting } from '../../../common/components/Waiting/Waiting';
import { useTranslation } from '../../../common/hooks/useTranslation';
import Button from '../../../common/components/Button/Button';
import FormRow from '../../../app/components/FormRow';
import CompanySelector from '../../components/Selects/CompanySelector';
import { SurveySelector } from '../../components/Selects/SurveySelector';
import { Option } from '../../components/Selects/Selector';

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => ({
  value: currentYear - i,
  label: String(currentYear - i),
}));

interface Props {
  withRemarks?: boolean;
}

export const ReportSurveyResultsView: React.FC<Props> = ({ withRemarks }) => {
  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('administrationLng');

  const [waiting, setWaiting] = useState(false);
  const [surveyId, setSurveyId] = useState<string | undefined>();
  const [year, setYear] = useState<number | undefined>();
  const [companyId, setCompanyId] = useState<string | undefined>();
  const [showAlert, setShowAlert] = useState(false);

  const onDownload = async () => {
    if (!surveyId) {
      setShowAlert(true);
      return;
    }
    setShowAlert(false);
    setWaiting(true);
    const currentDate = new Date().toISOString().split('T')[0];
    try {
      const fileKey = withRemarks
        ? tk.survey.surveyResultsWithRemarksFile
        : tk.survey.surveyResultsFile;
      const filename = `${t(fileKey)}_${currentDate}.xlsx`;
      if (withRemarks) {
        await pok.reports.reportSurveyResultsWithRemarks(
          filename,
          surveyId,
          year,
          companyId,
        );
      } else {
        await pok.reports.reportSurveyResults(
          filename,
          surveyId,
          year,
          companyId,
        );
      }
      setWaiting(false);
    } catch (error) {
      notifications.caughtError(error as ResponseError);
      setWaiting(false);
    }
  };

  return (
    <Card className="mb-2">
      <Card.Header>
        {withRemarks
          ? t(tk.clientSurvey.surveyResultsWithRemarksReport)
          : t(tk.survey.surveyResults)}
      </Card.Header>
      <Card.Body>
        <Form className="d-grid gap-3">
          <FormRow controlId="survey" label={t(tk.survey.survey)}>
            <SurveySelector
              value={surveyId}
              clearable={true}
              onChange={option => setSurveyId((option as Option)?.value)}
            />
          </FormRow>
          <FormRow controlId="year" label={t(tk.common.year)}>
            <Select
              value={YEAR_OPTIONS.find(o => o.value === year) ?? null}
              options={YEAR_OPTIONS}
              isClearable={true}
              onChange={option => setYear(option?.value ?? undefined)}
              placeholder={t(tk.component.pointOut)}
            />
          </FormRow>
          <FormRow controlId="company" label={t(tk.common.company)}>
            <CompanySelector
              value={companyId}
              clearable={true}
              onChange={option => setCompanyId((option as Option)?.value)}
            />
          </FormRow>
        </Form>

        {showAlert && (
          <Alert variant="danger" className="mt-3">
            {t(tk.survey.surveyRequired)}
          </Alert>
        )}

        {waiting ? (
          <Waiting />
        ) : (
          <div className="d-flex justify-content-end mt-3">
            <Button variant="outline-primary" onClick={onDownload}>
              {t(tk.button.export)}
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};
